import { dayjs } from '@shared/utils';
import { type FormState } from 'react-hook-form';

import { type DisplaySession, type Session } from './types';

export function calculateDuration(
  startedAt?: Date,
  endedAt?: Date,
): { hours: number; minutes: number } {
  if (!startedAt || !endedAt) {
    return { hours: 0, minutes: 0 };
  }

  const hours = dayjs(endedAt).diff(dayjs(startedAt), 'hours');
  const minutes = dayjs(endedAt).diff(dayjs(startedAt), 'minutes') % 60;

  return { hours, minutes };
}

function calculateTotalDuration(
  currentDuration: { hours: number; minutes: number },
  totalDuration: { hours: number; minutes: number },
) {
  currentDuration.hours = Math.max(currentDuration.hours, 0);
  currentDuration.minutes = Math.max(currentDuration.minutes, 0);

  totalDuration.hours += currentDuration.hours;
  totalDuration.minutes += currentDuration.minutes;

  if (totalDuration.minutes >= 60) {
    totalDuration.hours += Math.floor(totalDuration.minutes / 60);
    totalDuration.minutes = totalDuration.minutes % 60;
  }

  return totalDuration;
}

export function mapTimesToSessions(
  sessions: {
    startedAt?: Date;
    endedAt?: Date;
  }[] = [],
  keyMapByOrder: Map<number, string>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formState: FormState<any>,
) {
  return sessions.reduce(
    (acc, session, index) => {
      const order = index + 1;
      const formattedSession: Session = {
        order,
        key: `${keyMapByOrder.get(order) ?? ''}-${order}`,
        duration: calculateDuration(session.startedAt, session.endedAt),
        name: {
          start: `sessions.[${index}].startedAt`,
          end: `sessions.[${index}].endedAt`,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errorMessage: (formState.errors?.sessions as any)?.[index]?.root?.message,
      };

      acc.sessions.push(formattedSession);
      acc.totalDuration = calculateTotalDuration(formattedSession.duration, acc.totalDuration);

      return acc;
    },
    {
      sessions: [] as Session[],
      totalDuration: { hours: 0, minutes: 0 },
    },
  );
}

export function mapTimesToDisplaySessions(
  sessions: {
    round: number;
    startedAt: Date;
    endedAt: Date;
  }[] = [],
) {
  return sessions.reduce(
    (acc, session) => {
      const formattedSession = {
        order: session.round,
        startedAt: session.startedAt,
        endedAt: session.endedAt,
        duration: calculateDuration(session.startedAt, session.endedAt),
      };

      acc.sessions.push(formattedSession);
      acc.totalDuration = calculateTotalDuration(formattedSession.duration, acc.totalDuration);

      return acc;
    },
    {
      sessions: [] as DisplaySession[],
      totalDuration: { hours: 0, minutes: 0 },
    },
  );
}
